/**
 * Hook for triggering tenant sync
 */
import React from 'react';
import { Button, notification, Typography } from '@duxton-web/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TenantSyncConfig } from '../config';
import { NOTIFICATION_MESSAGES } from '../constants/messages';
import { queryKeys } from '@/hooks/constants';
import Service from '@/lib/App';
import { TenantSyncResponse } from '@/lib/PlannerService';

interface UseSyncToTenantOptions {
  onSuccess?: (data: TenantSyncResponse, variableName: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for syncing rollouts to a tenant
 * Creates a pending review in the tenant with the synced rollouts
 */
export function useSyncToTenant(
  config: TenantSyncConfig,
  options?: UseSyncToTenantOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variableName: string) => {
      return await Service.planner.syncRolloutToTenant(
        variableName,
        config.tenantSeq,
      );
    },
    onSuccess: (data, variableName) => {
      // Invalidate sync preview cache
      queryClient.invalidateQueries({
        queryKey: queryKeys.planner.tenantSyncPreview(
          config.name,
          variableName,
        ),
      });

      // Show success notification with raw message from backend
      const tenantUrl = config.getRolloutUrl(variableName);
      notification.success({
        message: 'Sync completed',
        description: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Typography.Text>{data.message}</Typography.Text>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              onClick={() =>
                window.open(tenantUrl, '_blank', 'noopener,noreferrer')
              }
            >
              {`Go to ${config.name} →`}
            </Button>
          </div>
        ),
        placement: 'bottomLeft',
        duration: 8,
      });

      options?.onSuccess?.(data, variableName);
    },
    onError: (error: any) => {
      // Extract arg from backend error response, fallback to error message
      const errorMessage =
        error?.response?.data?.arg || error.message || 'Unknown error';

      notification.error({
        message: NOTIFICATION_MESSAGES.syncError.message,
        description: errorMessage,
        placement: 'bottomLeft',
      });

      options?.onError?.(error);
    },
  });
}
